import type { CSSProperties, MouseEvent } from 'react'

/**
 * Compare Homes (#1713) as the card sees it: a few props and one mark, nothing
 * about selection state. The explorer's compare provider owns the picks; the
 * grid and slider hand each card a `CardCompare` only while compare mode is on,
 * so a card that gets none renders exactly the markup it always did.
 */
export interface CardCompare {
  picked: boolean
  /** The operator's button text, printed on the chip ("Compare"). */
  label: string
  /** The chip once picked ("Added"). */
  pickedLabel: string
  /** The chip's accessible name: "<button text> <home title>". */
  ariaLabel: string
  /** Accent variables for the chip and the picked ring, when the operator set an accent. */
  style?: CSSProperties
  onToggle: () => void
}

/**
 * Inside a card these keep working while compare mode is on: the slideshow
 * arrows and dots, in both card families. Everything else, the stretched link
 * included, picks the home instead of navigating.
 */
const PASS_THROUGH = [
  '.hr-pc-media__nav',
  '.hr-pc-media__dot',
  '.hfse-property-slideshow-prev-btn',
  '.hfse-property-slideshow-next-btn',
  '.hfse-property-slideshow-dotnav button',
].join(',')

/**
 * Attributes for the card's root element. Spread an empty object (so: nothing)
 * when compare is off. The capture handler is what makes the whole card the
 * target: it runs before the card link's own click handler and cancels the
 * navigation, so a pick never leaves the page.
 */
export function compareRootProps(compare: CardCompare | null | undefined): {
  'data-hr-compare'?: 'on' | 'picked'
  style?: CSSProperties
  onClickCapture?: (e: MouseEvent<HTMLElement>) => void
} {
  if (!compare) return {}
  return {
    'data-hr-compare': compare.picked ? 'picked' : 'on',
    style: compare.style,
    onClickCapture: (e) => {
      const target = e.target as Element | null
      if (target && typeof target.closest === 'function' && target.closest(PASS_THROUGH)) return
      e.preventDefault()
      e.stopPropagation()
      compare.onToggle()
    },
  }
}

function PlusIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" focusable="false">
      <path d="M10 4v12M4 10h12" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4.5 10.5l3.8 3.8 7.2-8" />
    </svg>
  )
}

/**
 * The select chip, in the favorites heart's corner (the heart steps aside while
 * the mode is on). A button with the checkbox role for keyboards and screen
 * readers; a pointer pick reaches the same handler through the root capture.
 */
export function CompareMark({ compare }: { compare: CardCompare }) {
  return (
    <button
      type="button"
      className="hrc-pick"
      data-hr-compare-mark=""
      role="checkbox"
      aria-checked={compare.picked}
      aria-label={compare.ariaLabel}
      style={compare.style}
    >
      <span className="hrc-pick__face hrc-pick__add"><PlusIcon />{compare.label}</span>
      <span className="hrc-pick__face hrc-pick__added"><CheckIcon />{compare.pickedLabel}</span>
    </button>
  )
}
